import { type CSSProperties, type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CatalogModelOption } from "@cyh/contracts";
import { api } from "../../api";
import { useLanguage } from "../../i18n";
import { formatSpecifications } from "./Shared";

export function CatalogItemCombobox({ value, initialText = "", onChange }: { value: CatalogModelOption | null; initialText?: string; onChange: (value: CatalogModelOption | null) => void }) {
  const { t, language } = useLanguage();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  const emittedValueId = useRef(value?.id);
  const [query, setQuery] = useState(value ? catalogItemOptionLabel(value, language) : initialText);
  const [options, setOptions] = useState<CatalogModelOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useEffect(() => {
    if (emittedValueId.current === value?.id) return;
    emittedValueId.current = value?.id;
    setQuery(value ? catalogItemOptionLabel(value, language) : initialText);
  }, [value?.id, initialText, language]);
  useEffect(() => {
    if (!open) return;
    const currentRequest = ++requestId.current;
    const selectedLabel = value ? catalogItemOptionLabel(value, language) : "";
    const search = query === selectedLabel ? "" : query.trim();
    const timer = window.setTimeout(() => {
      setLoading(true); setFailed(false);
      void api.catalogModelOptions(search, 20).then((rows) => {
        if (requestId.current !== currentRequest) return;
        setOptions(rows); setActiveIndex(0);
      }).catch(() => {
        if (requestId.current !== currentRequest) return;
        setOptions([]); setFailed(true);
      }).finally(() => { if (requestId.current === currentRequest) setLoading(false); });
    }, search ? 220 : 0);
    return () => window.clearTimeout(timer);
  }, [open, query, value?.id, language]);
  useLayoutEffect(() => {
    if (!open) return;
    function positionMenu() {
      const rectangle = rootRef.current?.getBoundingClientRect();
      if (!rectangle) return;
      const margin = 12;
      const gap = 5;
      const maximumWidth = Math.min(560, Math.max(0, window.innerWidth - margin * 2));
      const width = Math.min(maximumWidth, Math.max(Math.min(320, maximumWidth), rectangle.width));
      const left = Math.max(margin, Math.min(rectangle.left, window.innerWidth - margin - width));
      const spaceBelow = window.innerHeight - rectangle.bottom - gap - margin;
      const spaceAbove = rectangle.top - gap - margin;
      const opensAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      const availableHeight = Math.max(100, opensAbove ? spaceAbove : spaceBelow);
      setMenuStyle({
        left,
        width,
        maxHeight: Math.min(310, availableHeight),
        top: opensAbove ? "auto" : rectangle.bottom + gap,
        bottom: opensAbove ? window.innerHeight - rectangle.top + gap : "auto"
      });
    }
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open]);

  function select(option: CatalogModelOption) { emittedValueId.current = option.id; onChange(option); setQuery(catalogItemOptionLabel(option, language)); setOpen(false); }
  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActiveIndex((index) => Math.min(index + 1, Math.max(0, options.length - 1))); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
    else if (event.key === "Enter" && open && options[activeIndex]) { event.preventDefault(); select(options[activeIndex]!); }
    else if (event.key === "Escape") setOpen(false);
  }

  return <div ref={rootRef} className="model-combobox" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <input className="model-combobox-input" required autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} value={query} onFocus={(event) => { setOpen(true); if (value || initialText) event.currentTarget.select(); }} onChange={(event) => { emittedValueId.current = undefined; setQuery(event.target.value); onChange(null); setOpen(true); }} onKeyDown={handleKeyDown} placeholder={t("输入物料编码、名称、品牌或规格", "Type item code, name, brand, or specification")} />
    {open && createPortal(<div className="model-combobox-menu" id={listId} role="listbox" style={menuStyle}>
      {loading ? <div className="model-combobox-state">{t("正在搜索…", "Searching…")}</div> : failed ? <div className="model-combobox-state error">{t("物料搜索失败，请重试", "Item search failed. Try again.")}</div> : options.length ? options.map((option, index) => <button type="button" role="option" aria-selected={value?.id === option.id} className={index === activeIndex ? "active" : ""} key={option.id} onMouseEnter={() => setActiveIndex(index)} onMouseDown={(event) => { event.preventDefault(); select(option); }}><strong>{option.itemCode} · {localizedCatalogItemName(option, language)}</strong>{option.brand && <span>{option.brand}</span>}<small>{formatSpecifications(option, language)}</small></button>) : <div className="model-combobox-state">{t("没有匹配的物料", "No matching items")}</div>}
    </div>, document.body)}
  </div>;
}

export function catalogItemOptionLabel(option: CatalogModelOption, language: "zh-CN" | "en" = "zh-CN"): string {
  return [option.itemCode, localizedCatalogItemName(option, language), option.brand].filter(Boolean).join(" · ");
}

export function localizedCatalogItemName(option: Pick<CatalogModelOption, "itemName" | "itemNameEn">, language: "zh-CN" | "en"): string {
  return language === "en" ? option.itemNameEn || option.itemName : option.itemName;
}
